import { useMemo } from 'react'
import type { ItemView } from './DataPanel.js'

export interface Suggestion {
  /** Что показать в списке. */
  label: string
  /** Что дописать вместо набранного начала. */
  insert: string
  /** Сколько символов перед курсором заменить. */
  cut: number
  /** Значение из первого элемента: по имени поле узнаётся хуже, чем по нему. */
  hint?: string
}

export type SuggestionKind = 'fields' | 'nodes' | 'none'

export interface SuggestionSet {
  kind: SuggestionKind
  suggestions: Suggestion[]
}

const NODE_CONTEXT = /\$node\[\s*"([^"\]]*)$/u
const JSON_CONTEXT = /\$json((?:\.[\p{L}\p{N}_]+|\["[^"]*"\])*)\.([\p{L}\p{N}_]*)$/u
const SIMPLE_NAME = /^[\p{L}_][\p{L}\p{N}_]*$/u

/** Разбирает уже набранный путь: `.contact["дом 1"]` → ['contact', 'дом 1']. */
function parsePath(source: string): string[] {
  const segments: string[] = []
  const pattern = /\.([\p{L}\p{N}_]+)|\["([^"]*)"\]/gu
  let match: RegExpExecArray | null
  while ((match = pattern.exec(source)) !== null) {
    segments.push(match[1] ?? match[2] ?? '')
  }
  return segments
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Значение по пути внутри элемента. */
function valueAt(json: Record<string, unknown>, path: readonly string[]): unknown {
  let current: unknown = json
  for (const segment of path) {
    if (!isPlainObject(current)) return undefined
    if (!Object.prototype.hasOwnProperty.call(current, segment)) return undefined
    current = current[segment]
  }
  return current
}

function preview(value: unknown): string | undefined {
  if (value === undefined) return undefined
  if (typeof value === 'string') return value
  return JSON.stringify(value) ?? undefined
}

/**
 * Поля по этому пути во всех элементах прогона. Объединение, а не поля
 * первого элемента: у второго сообщения может быть поле, которого у первого
 * нет, и предлагать только пересечение значило бы скрывать существующее.
 */
function fieldsAt(items: readonly ItemView[], path: readonly string[]): string[] {
  const found: string[] = []
  for (const item of items) {
    const target = valueAt(item.json, path)
    if (!isPlainObject(target)) continue
    for (const key of Object.keys(target)) {
      if (!found.includes(key)) found.push(key)
    }
  }
  return found
}

/**
 * Что предложить в позиции курсора. Поля берутся из данных последнего
 * прогона (FR-UI-003) и только из них: без прогона список пуст, а не
 * заполнен догадками по типу параметра. Догадка выглядит как знание — и
 * автор сценария узнаёт, что поля нет, только когда прогон упадёт.
 */
export function suggestionsFor(
  text: string,
  caret: number,
  items: readonly ItemView[],
  nodeNames: readonly string[],
): SuggestionSet {
  const before = text.slice(0, caret)
  const after = text.slice(caret)

  const node = NODE_CONTEXT.exec(before)
  if (node !== null) {
    const partial = node[1] ?? ''
    // Закрывающие кавычка и скобка дописываются, только если их ещё нет:
    // иначе на месте курсора окажется `"]"]`.
    const tail = after.startsWith('"') ? '' : '"]'
    return {
      kind: 'nodes',
      suggestions: nodeNames
        .filter((name) => name.toLowerCase().startsWith(partial.toLowerCase()))
        .map((name) => ({ label: name, insert: `${name}${tail}`, cut: partial.length })),
    }
  }

  const json = JSON_CONTEXT.exec(before)
  if (json !== null) {
    const path = parsePath(json[1] ?? '')
    const partial = json[2] ?? ''
    const first = items[0]
    return {
      kind: 'fields',
      suggestions: fieldsAt(items, path)
        .filter((field) => field.toLowerCase().startsWith(partial.toLowerCase()))
        .map((field) => {
          const value = first === undefined ? undefined : valueAt(first.json, [...path, field])
          const hint = preview(value)
          return {
            label: field,
            // Непростое имя пишется скобками вместе с точкой перед ним:
            // `$json.заказ-1` разобралось бы как вычитание.
            ...(SIMPLE_NAME.test(field)
              ? { insert: field, cut: partial.length }
              : { insert: `[${JSON.stringify(field)}]`, cut: partial.length + 1 }),
            ...(hint === undefined ? {} : { hint }),
          }
        }),
    }
  }

  return { kind: 'none', suggestions: [] }
}

/** Текст и позиция курсора после выбора подсказки. */
export function applySuggestion(
  text: string,
  caret: number,
  suggestion: Suggestion,
): { value: string; caret: number } {
  const head = text.slice(0, caret - suggestion.cut)
  return { value: `${head}${suggestion.insert}${text.slice(caret)}`, caret: head.length + suggestion.insert.length }
}

export interface ExpressionAutocompleteProps {
  value: string
  /** Позиция курсора; по умолчанию конец строки. */
  caret?: number
  /** Элементы последнего прогона на входе этой ноды. */
  items?: readonly ItemView[]
  /** Имена нод графа: к ним обращается `$node["Имя"]`. */
  nodeNames?: readonly string[]
  /** Готовый текст и место курсора. Удобен, когда поле одно. */
  onPick?: (value: string, caret: number) => void
  /**
   * Сама подсказка. Нужен вызывающему, который держит текст сам (панель
   * параметров правит значение узла, а не строку компонента).
   */
  onPickSuggestion?: (suggestion: Suggestion) => void
}

/**
 * Подсказки для выражения. Поля предлагаются только те, что действительно
 * пришли в прогоне (FR-UI-003): выдумать список по типу параметра значит
 * подсказать поле, которого в данных нет.
 */
export function ExpressionAutocomplete({
  value,
  caret,
  items = [],
  nodeNames = [],
  onPick,
  onPickSuggestion,
}: ExpressionAutocompleteProps): React.JSX.Element | null {
  const position = caret ?? value.length
  const { kind, suggestions } = useMemo(
    () => suggestionsFor(value, position, items, nodeNames),
    [value, position, items, nodeNames],
  )

  if (kind === 'none') return null

  return (
    <div className="подсказки" role="listbox" aria-label="Подсказки выражения">
      {suggestions.length === 0 ? (
        <p className="подсказки__пусто">
          {kind === 'fields' && items.length === 0
            ? 'Полей пока нет: они появятся после прогона сценария.'
            : 'Ничего подходящего.'}
        </p>
      ) : (
        suggestions.map((suggestion) => (
          <button
            key={suggestion.label}
            type="button"
            role="option"
            aria-selected="false"
            onClick={() => {
              onPickSuggestion?.(suggestion)
              const next = applySuggestion(value, position, suggestion)
              onPick?.(next.value, next.caret)
            }}
          >
            <span className="подсказка__имя">{suggestion.label}</span>
            {suggestion.hint !== undefined ? (
              <span className="подсказка__значение">{suggestion.hint}</span>
            ) : null}
          </button>
        ))
      )}
    </div>
  )
}
